import React from 'react';
import { Close } from '@att-bit/duc.behaviors.close';
import { useState, useEffect, useRef } from "react";
import { ConditionalWrapper } from '@att-bit/duc.utils.conditional-wrapper';
import { Scrim } from '@att-bit/duc.fragments.scrim';
import { AnalyticsEvent } from '@att-bit/ui-frameworks.components.analytics-event';
import { docComplete } from '@att-bit/duc.utils.types';

export enum ModalSize {
  SMALL = 'SMALL',
  MEDIUM = 'MEDIUM',
  LARGE = 'LARGE',
  XLARGE = 'XLARGE',
  // FULLWIDTH = 'FULLWIDTH',
  FULLSCREEN = 'FULLSCREEN',
};

export enum ModalVariant {
  DEFAULT = 'DEFAULT',
  IMAGE = 'IMAGE',
  SCROLL = 'SCROLL',
};

// export const MODAL_INVISIBLE = 'opacity0';
// export const MODAL_VISIBLE = 'opacity1';
const FOCUSABLE_SELECTORS = 'input, button, a, select, textarea, div[tabindex="0"], div[role="button"]:not([aria-hidden=true]), div[role="checkbox"]:not([aria-hidden=true]), div[role="radio"]:not([aria-hidden=true]), summary';
const MARGIN = 24;
const MODAL_SIZES = {
  [ModalSize.SMALL]: 400,
  [ModalSize.MEDIUM]: 560,
  [ModalSize.LARGE]: 720,
  [ModalSize.XLARGE]: 960,
  // [ModalSize.FULLWIDTH]: 0,
  [ModalSize.FULLSCREEN]: 0,
}
export interface ModalProps extends React.HTMLAttributes<HTMLDivElement> {
  /**  children (text/jsx) to be rendered in component  */
  children: React.ReactNode;
  /** show close button or not */
  close?: boolean;
  /** callback for close event */
  onClose: () => void;
  /** go full screen when screen size reaches modal size */
  responsiveFullScreen?: boolean;
  /** size enum for content width/margin of modal */
  size?: ModalSize;
  /** variant */
  variant?: ModalVariant;
  /** for fullpage: use true if the spinner is in a custom scrim, false spinner will provide its own scrim */
  inScrim?: boolean;
  /** used to allow fade out before close */
  show?: boolean;
  /** callback for modal visible */
  mounted?: () => void;
  /** close aria-label html attribute */
  closeAriaLabel?: string;
  /** Custom scrim zindex by default 4 */
  scrimZindex?: number;
  /** additional page load analytics data*/
  additionalAnalyticsData?: any;
  /** Modal has no children with Modal.Header */
  noModalHeader?: boolean;
};

export const Modal = ({
  children,
  close = true,
  onClose,
  responsiveFullScreen = false,
  size = ModalSize.LARGE,
  variant = ModalVariant.DEFAULT,
  inScrim = false,
  show = true,
  mounted = undefined,
  closeAriaLabel = undefined,
  scrimZindex = 4,
  additionalAnalyticsData,
  noModalHeader = false,
  ...props
}: ModalProps & React.HTMLAttributes<HTMLDivElement>) => {

  // HOOKS ##############################################################

  const [showScrim, setShowScrim] = useState(inScrim === true ? false : true);
  const returnFocusRef = useRef(null);
  const focusableElementsRef = useRef(null);
  const divRef = useRef(null);
  const modalRef = useRef(null);
  const sizeMap = {};
  Object.keys(MODAL_SIZES).forEach((key) => {
    sizeMap[key] = {
      maxWidth: MODAL_SIZES[key] ? MODAL_SIZES[key] + 'px' : 'none',
      maxWidthMargins: MODAL_SIZES[key] ? (MODAL_SIZES[key] + 60) + 'px' : '0',
    }
  })
  // const sizeMap = useMemo(() => {
  //   return Object.keys(MODAL_SIZES).map((key) => {
  //     return {
  //      maxWidth: MODAL_SIZES[key] ? MODAL_SIZES[key] + 'px' : 'none',
  //      maxWidthMargins: MODAL_SIZES[key] ? (MODAL_SIZES[key] + MARGIN * 2) + 'px' : '0',
  //    }
  //   })
  // }, [])

  useEffect(() => {
    // istanbul ignore next // TODO
    focusableElementsRef.current = modalRef?.current?.querySelectorAll(FOCUSABLE_SELECTORS);
    returnFocusRef.current = document.activeElement;  
      divRef.current?.focus();
    if(!close){
      focusableElementsRef.current ? focusableElementsRef.current[0] ? focusableElementsRef.current[0].focus() : null : null;      
    }
   
    window.addEventListener("keydown", onKeyDown, false);
    return () => {
      window.removeEventListener("keydown", onKeyDown, false);
    }
  }, []);

  useEffect(() => {
    if (show) {
      mounted?.();
      if (additionalAnalyticsData) {
        const pageLoadObj = {
          "eventCode": "Page_Load",
          "eventAction": "pageLoad",
          "additionalData": additionalAnalyticsData
        }
        docComplete(()=>{
          AnalyticsEvent(pageLoadObj);
        });
      }
    } else {
      setShowScrim(false);
    }
  }, [show]);

  const onKeyDown = (e: KeyboardEvent) => {
    // istanbul ignore else // need teo do tabs...
    if (e.key === "Escape") {
      requestToClose();
    } else if (e.key === "Tab") {
      let focusableEl = [];
      if (focusableElementsRef.current && focusableElementsRef.current.length > 0) {
        focusableEl = focusableElementsRef.current;
      } else {
        focusableEl = modalRef?.current?.querySelectorAll(FOCUSABLE_SELECTORS);
        focusableElementsRef.current = focusableEl;
      }
      const updatedFocusableElements = Array.from(
        focusableEl
      ).filter((el: HTMLElement) => {
        let parentElement = el.parentElement;
        while (parentElement && parentElement !== modalRef.current) {
          if (parentElement.tagName === "DETAILS" && !(parentElement as HTMLDetailsElement).open) {
            // Check if the element is the direct summary child of a closed details
            if (
              !(el.tagName === "SUMMARY" && el.parentElement === parentElement)
            ) {
              return false;
            }
          }
          parentElement = parentElement.parentElement;
        }
        return true;
      });

      const firstElement = updatedFocusableElements[0] as HTMLElement;
      const lastElement =
        updatedFocusableElements[updatedFocusableElements.length - 1] as HTMLElement;

      // If the shift key is pressed and the current active element is the first one, move focus to the last element
      if (e.shiftKey && document.activeElement === firstElement) {
        lastElement.focus();
        e.preventDefault();
      }

      // If the shift key isn't pressed and the current active element is the last one, move focus to the first element
      else if (!e.shiftKey && document.activeElement === lastElement) {
        firstElement.focus();
        e.preventDefault();
      }
    }
  };

  const requestToClose = () => {
    if (close) {
      if (inScrim) {
        doClose();
      } else {
        setShowScrim(false);
      }
    }
  }

  const doClose = () => {
    returnFocusRef.current.focus();
    onClose();
  }

  const contentClass = [
    'duc-modal-content width-full overflow-hidden rel bg-ui-white rel',
    size == ModalSize.FULLSCREEN ? 'duc-modal-full-screen' : undefined,
    size !== ModalSize.FULLSCREEN && variant !== ModalVariant.SCROLL ? 'duc-modal-content-standard' : undefined,
    responsiveFullScreen ? 'duc-modal-content-responsive' : undefined,
  ].join(' ');

  let hasImage = false;

  const jsx = (
      <div
        {...props}      
        className="fixed top left height-full width-full flex flex-items-center justify-center"
        ref={modalRef}
        role={!noModalHeader ? (close? "dialog":"alertdialog") : undefined}
        aria-labelledby={!noModalHeader ? "dialogHeading" : undefined}
        aria-describedby="dialogContent"
        onClick={(event) => {
          /* istanbul ignore next */
          if (event.target === modalRef?.current) {
            requestToClose();
          }
        }}
        style={{zIndex: scrimZindex + 1}}
      >
        <div
          className={contentClass}
          tabIndex={-1}
          ref={divRef}
        >
          {React.Children.map(children, (child) => {
            if (React.isValidElement(child) && (child.type === Modal.Wrapper || child.type === Modal.Header || child.type === Modal.Image)) {
              if (ModalVariant.IMAGE === variant || child.type === Modal.Image) {
                hasImage = true;
                return (
                  <div className="text-center">
                    {child}
                  </div>
                )
              }
              return child
            }
          })}

          {close &&
              <Close
                onClick={requestToClose}
                aria-label={closeAriaLabel}
                bgClassName={hasImage ? 'bg-white' : 'transparent'}
              />
          }

          {React.Children.map(children, (child) => {
            if (React.isValidElement(child) && child.type === Modal.Body) {
              return (
                <ConditionalWrapper
                  condition={ModalVariant.SCROLL === variant}
                  wrapper={children => <div className="duc-modal-content-scrollable">{children}</div>}
                >
                  {child}
                </ConditionalWrapper>
              );
            }
          })}
        </div>
      </div>
  );

  return (
    <>
      <ConditionalWrapper
        condition={inScrim !== true}
        wrapper={children => (
          <Scrim
            show={showScrim}
            onClick={() => {
              // istanbul ignore else
              if (close) {
                setShowScrim(false)
              }
            }}
            mounted={(visible) => {
              // istanbul ignore else
              if (!visible) {
                doClose();
              }
            }}
            customZindex={scrimZindex}
          >
            {children}
          </Scrim>
        )}
      >
        {jsx}
      </ConditionalWrapper>
      <style global jsx>{`
          .duc-modal-full-screen {
            height: 100vh;
            width: 100vw;
            border-radius: 0px !important;
            overflow-y: auto !important;
          }      
          .duc-modal-content-standard {
            max-height: 90vh;
            overflow-y: auto !important;
          }
          .duc-modal-content-wrapper {
            max-height: 90vh;
          }
          .duc-modal-content-scrollable {
            max-height: calc(90vh - 120px);
            overflow-y: scroll;
          }
      `}</style>
      <style global jsx>{`
        .duc-modal-content {
          max-width: ${sizeMap[size].maxWidth};
          border-radius: 16px;
        }
        @media (max-width: ${sizeMap[size].maxWidthMargins}) {
          .duc-modal-content {
              margin-left: ${MARGIN}px;
              margin-right: ${MARGIN}px;
          }
        }
        @media (max-width: ${sizeMap[size].maxWidth}) {
          .duc-modal-content-responsive {
            height: 100vh;
            max-height: 100vh;
            width: 100vw;
            border-radius: 0px;
            margin-left: 0px;
            margin-right: 0px;
            overflow-y: auto !important;
          }
        }
        @media (max-width: 475px) {
          .flex-column-reverse {
            flex-direction: column-reverse;
          }
          .button-wrap {
            margin-left: 0 !important;
            margin-right: 0 !important;
          }
          #dialogContent button {
            width: 100%;
          }
        }
        `}</style>
    </>
  )
}

const ModalWrapper = ({ children }) => {
  return (
    <div className="overflow-y-auto duc-modal-content-wrapper">
      {children}
    </div>
  );
}
Modal.Wrapper = ModalWrapper;

const ModalImage = ({ children }) => {
  return (
    <div className="mar-b-md">
      {children}
    </div>
  );
}
Modal.Image = ModalImage;

const ModalHeader = ({ children }) => {
  return (
    <h2 id="dialogHeading" className="heading-md mar-b-xs mar-t-md-all pad-l-md-lg pad-r-md-lg pad-l-lg-md pad-r-lg-md pad-r-lg-sm pad-l-lg-sm">
      {children}
    </h2>
  );
}
Modal.Header = ModalHeader;

const ModalBody = ({ children, legalModal=false }) => {
  return (
    <div id="dialogContent" className={`pad-b-md-lg pad-l-md-lg pad-r-md-lg pad-l-lg-md pad-r-lg-md pad-r-lg-sm pad-l-lg-sm${legalModal?" type-sm" : ""}`}>
      {children}
    </div>
  );
}
Modal.Body = ModalBody;
